import { useEffect, useRef, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'workwav_pwa_install_dismissed';

/**
 * Handles PWA installation across platforms:
 * - Chrome / Edge / Android → captures beforeinstallprompt and exposes promptInstall()
 * - iOS Safari             → sets isIOS = true so the UI can show a manual guide
 * - Already installed      → isInstalled = true, banner hidden
 */
export const usePWAInstall = () => {
  const [canInstall,  setCanInstall]  = useState(false);
  const [isIOS,       setIsIOS]       = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Already running as installed PWA?
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // User already dismissed the banner this session / ever
    if (localStorage.getItem(DISMISSED_KEY) === 'true') return;

    // Detect iOS (Safari doesn't fire beforeinstallprompt)
    const ua = window.navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream;
    if (isIOSDevice) {
      setIsIOS(true);
      setCanInstall(true);
      return;
    }

    // Chrome / Edge / Samsung Browser / Android Chrome
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault(); // stop browser's mini-bar
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    const handleInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      deferredPrompt.current = null;
    };
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  /** Triggers the native install dialog (Chrome/Edge/Android only). */
  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt.current) return false;
    await deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    if (outcome === 'accepted') {
      setCanInstall(false);
      setIsInstalled(true);
    }
    return outcome === 'accepted';
  };

  /** Permanently hides the install banner for this user. */
  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setCanInstall(false);
  };

  return { canInstall, isIOS, isInstalled, promptInstall, dismiss };
};
